import React from "react";
import { motion } from "framer-motion";

import { FaCode, FaReact, FaNodeJs, FaCss3Alt, FaHtml5, FaJs, FaGitAlt, FaGithub, FaAws, FaDocker } from "react-icons/fa";
import { SiCplusplus, SiCsharp, SiPython, SiTypescript, SiTailwindcss, SiMysql, SiRedis, SiSocketdotio, SiRedux, SiDotnet, SiFastapi, SiRabbitmq, SiAmazondynamodb } from "react-icons/si";
import { GiBrain } from "react-icons/gi";

const categoryColors = {
  lang:  { accent: "from-blue-400 to-cyan-400",   border: "border-blue-500/30",  hoverBorder: "hover:border-blue-400/60",  iconColor: "group-hover:text-blue-400",  tagBg: "bg-blue-500/10 text-blue-400 border-blue-500/20",  glow: "group-hover:shadow-blue-500/10" },
  arch:  { accent: "from-violet-400 to-purple-400", border: "border-violet-500/30", hoverBorder: "hover:border-violet-400/60", iconColor: "group-hover:text-violet-400", tagBg: "bg-violet-500/10 text-violet-400 border-violet-500/20", glow: "group-hover:shadow-violet-500/10" },
  back:  { accent: "from-emerald-400 to-green-400",  border: "border-emerald-500/30", hoverBorder: "hover:border-emerald-400/60", iconColor: "group-hover:text-emerald-400", tagBg: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", glow: "group-hover:shadow-emerald-500/10" },
  front: { accent: "from-cyan-400 to-teal-400",    border: "border-cyan-500/30",    hoverBorder: "hover:border-cyan-400/60",    iconColor: "group-hover:text-cyan-400",    tagBg: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",    glow: "group-hover:shadow-cyan-500/10" },
  ops:   { accent: "from-orange-400 to-amber-400",  border: "border-orange-500/30",  hoverBorder: "hover:border-orange-400/60",  iconColor: "group-hover:text-orange-400",  tagBg: "bg-orange-500/10 text-orange-400 border-orange-500/20",  glow: "group-hover:shadow-orange-500/10" },
};

const skills = [
  {
    category: "Languages",
    mono: "lang",
    skills: [
      { name: "C++", icon: <SiCplusplus /> },
      { name: "C", icon: <FaCode /> },
      { name: "C#", icon: <SiCsharp /> },
      { name: "Python", icon: <SiPython /> },
      { name: "TypeScript", icon: <SiTypescript /> },
      { name: "JavaScript", icon: <FaJs /> },
      { name: "SQL", icon: <SiMysql /> },
    ],
  },
  {
    category: "Architecture",
    mono: "arch",
    skills: [
      { name: "Microservices", icon: <FaCode /> },
      { name: "Systems Design", icon: <GiBrain /> },
      { name: "Event-Driven", icon: <FaCode /> },
      { name: "Clean Arch", icon: <FaCode /> },
    ],
  },
  {
    category: "Backend",
    mono: "back",
    skills: [
      { name: ".NET Core", icon: <SiDotnet /> },
      { name: "FastAPI", icon: <SiFastapi /> },
      { name: "Node.js", icon: <FaNodeJs /> },
      { name: "REST APIs", icon: <FaCode /> },
      { name: "Redis", icon: <SiRedis /> },
      { name: "RabbitMQ", icon: <SiRabbitmq /> },
      { name: "SNS/SQS", icon: <FaAws /> },
    ],
  },
  {
    category: "Frontend",
    mono: "front",
    skills: [
      { name: "React", icon: <FaReact /> },
      { name: "Redux", icon: <SiRedux /> },
      { name: "Tailwind", icon: <SiTailwindcss /> },
      { name: "WebSockets", icon: <SiSocketdotio /> },
      { name: "HTML5", icon: <FaHtml5 /> },
      { name: "CSS3", icon: <FaCss3Alt /> },
    ],
  },
  {
    category: "DevOps & Cloud",
    mono: "ops",
    skills: [
      { name: "AWS", icon: <FaAws /> },
      { name: "Docker", icon: <FaDocker /> },
      { name: "DynamoDB", icon: <SiAmazondynamodb /> },
      { name: "Git", icon: <FaGitAlt /> },
      { name: "GitHub", icon: <FaGithub /> },
      { name: "CI/CD", icon: <FaCode /> },
    ],
  },
];

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.06 } } };
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] } } };

const Skills = () => {
  const totalSkills = skills.reduce((sum, cat) => sum + cat.skills.length, 0);

  return (
    <div className="relative text-white max-w-6xl mx-auto px-6 py-16 overflow-hidden">
      <div className="noise-overlay"></div>

      {/* Ambient glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-40 w-[400px] h-[400px] bg-blue-600/6 rounded-full blur-[120px] animate-drift"></div>
        <div className="absolute bottom-20 -right-40 w-[400px] h-[400px] bg-violet-600/5 rounded-full blur-[120px] animate-drift-reverse"></div>
      </div>

      {/* Header with stats */}
      <div className="relative z-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <p className="text-[10px] text-gray-600 uppercase tracking-[0.4em] font-mono mb-2">05 / Skills</p>
          <h2 className="text-4xl md:text-5xl font-black tracking-tight">Technical<br /><span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500">arsenal</span></h2>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.3 }} className="flex gap-6 mb-2">
          <div className="text-right">
            <div className="text-2xl font-black text-white tabular-nums">{totalSkills}</div>
            <div className="text-[10px] text-gray-600 uppercase tracking-widest font-mono">technologies</div>
          </div>
          <div className="w-px bg-gray-800"></div>
          <div className="text-right">
            <div className="text-2xl font-black text-white tabular-nums">{skills.length}</div>
            <div className="text-[10px] text-gray-600 uppercase tracking-widest font-mono">domains</div>
          </div>
        </motion.div>
      </div>

      {/* Skills sections */}
      <div className="relative z-10 mt-16 space-y-10">
        {skills.map((cat, catIndex) => {
          const colors = categoryColors[cat.mono];
          return (
            <motion.div key={catIndex} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: catIndex * 0.08 }}
              className="relative"
            >
              {/* Category label with colored accent */}
              <div className="flex items-center gap-3 mb-5">
                <span className={`font-mono text-[10px] px-2.5 py-1 rounded border ${colors.tagBg}`}>{cat.mono}</span>
                <span className="text-sm font-semibold text-gray-300">{cat.category}</span>
                <div className={`flex-1 h-px bg-gradient-to-r ${colors.accent} opacity-10`}></div>
                <span className="text-[10px] text-gray-600 font-mono bg-gray-800/40 px-2 py-0.5 rounded">{cat.skills.length}</span>
              </div>

              {/* Skill chips with color-coded hover */}
              <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="flex flex-wrap gap-3">
                {cat.skills.map((skill, i) => (
                  <motion.div key={i} variants={fadeUp}
                    className={`group flex items-center gap-2.5 px-4 py-2.5 rounded-lg bg-white/[0.02] border border-gray-800/50 ${colors.hoverBorder} backdrop-blur-sm cursor-default transition-all duration-400 hover:bg-white/[0.05] hover:shadow-lg ${colors.glow} hover:-translate-y-0.5`}
                  >
                    <span className={`text-base text-gray-600 ${colors.iconColor} transition-colors duration-400`}>{skill.icon}</span>
                    <span className="text-sm text-gray-400 group-hover:text-white transition-colors duration-400 font-medium">{skill.name}</span>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          );
        })}
      </div>

      {/* Bottom marquee — colorful flowing ticker */}
      <div className="relative z-10 mt-20 overflow-hidden py-5">
        <div className="absolute inset-0 bg-gradient-to-r from-[#020024] via-transparent to-[#020024] z-10 pointer-events-none"></div>
        <div className="flex gap-6 animate-marquee-slow whitespace-nowrap">
          {[...skills.flatMap(c => c.skills.map(s => ({ name: s.name, mono: c.mono }))), ...skills.flatMap(c => c.skills.map(s => ({ name: s.name, mono: c.mono })))].map((item, i) => {
            const colors = categoryColors[item.mono];
            return (
              <span key={i} className="flex items-center gap-2">
                <span className={`w-1 h-1 rounded-full bg-gradient-to-r ${colors.accent}`}></span>
                <span className="text-xs font-mono text-gray-600 uppercase tracking-wider">{item.name}</span>
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Skills;
